import { DEFAULT_PRODUCT_IMAGE_URL, IMAGEKIT_ID } from '@/libs/config/client'
import { selectDetailMenuSite } from '@/redux/meta/selector'
import { isEmpty } from '@/utils/string'
import { stripUploadcareFilename } from '@/utils/uploadcare'
import { useSelector } from 'react-redux'

export const retrieveProductImagePath = ({
  url,
  returnDefault,
  options,
  enableImageKit,
  imagePath,
}: {
  url?: string | null
  returnDefault?: boolean
  options?: {
    shouldCache: boolean
    containerWidth?: number
    containerHeight?: number
    objectFit?: string
    useUcareCdnScaleCrop?: boolean
  }
  enableImageKit?: boolean
  imagePath?: string
}) => {
  const {
    shouldCache,
    containerWidth,
    containerHeight,
    objectFit,
    useUcareCdnScaleCrop,
  } = options || {}

  if (isEmpty(url) || url === DEFAULT_PRODUCT_IMAGE_URL) {
    return returnDefault ? DEFAULT_PRODUCT_IMAGE_URL : null
  }

  if (url?.includes('ucarecdn')) {
    const ucareUrl = stripUploadcareFilename(url)
    // imageKitWidth and imageKitHeight control how the image needs to be resized
    const imageUcareWidth =
      containerWidth !== undefined ? `${containerWidth}x` : ''
    const imageUcareHeight =
      containerHeight !== undefined
        ? `${containerWidth === undefined ? 'x' : ''}${containerHeight}`
        : ''
    return `${ucareUrl}${
      imageUcareWidth || imageUcareHeight
        ? `/-/${
            useUcareCdnScaleCrop ? 'scale_crop' : 'resize'
          }/${imageUcareWidth}${imageUcareHeight}/${
            useUcareCdnScaleCrop ? 'smart/' : ''
          }`
        : ''
    }`
  }

  if (!shouldCache) {
    return returnDefault ? DEFAULT_PRODUCT_IMAGE_URL : null
  }

  if (enableImageKit) {
    // imageKitWidth and imageKitHeight control how the image needs to be resized
    const imageKitWidth =
      containerWidth !== undefined ? `w-${containerWidth}` : ''
    const imageKitHeight =
      containerHeight !== undefined ? `h-${containerHeight}` : ''
    if (url?.includes('http')) {
      // return `https://ik.imagekit.io/${IMAGEKIT_ID}/${url}?tr=${imageKitWidth},${imageKitHeight}`
      return url
    }
    return `https://ik.imagekit.io/${IMAGEKIT_ID}/${imagePath}product/${url}.jpg?tr=${imageKitWidth},${imageKitHeight}`
  }

  // weserveWidth and weserveHeight control how the image needs to be resized
  const weserveWidth =
    containerWidth !== undefined ? `&w=${containerWidth}` : ''
  const weserveHeight =
    containerHeight !== undefined ? `&h=${containerHeight}` : ''
  const weserveFit = !isEmpty(objectFit) ? `&fit=${objectFit}` : '&fit=cover'
  if (url?.includes('http')) {
    return `https://images.weserv.nl/?url=${url}${weserveWidth}${weserveHeight}${weserveFit}`
  }
  return `https://images.weserv.nl/?url=${imagePath}product/${url}.jpg${weserveWidth}${weserveHeight}${weserveFit}`
}

const useGetProductImagePath = () => {
  const { enableImageKit, imagePath } = useSelector(selectDetailMenuSite) || {}

  const getProductImagePath = (
    url?: string | null,
    returnDefault?: boolean,
    options?: {
      shouldCache: boolean
      containerWidth?: number
      containerHeight?: number
      objectFit?: string
      useUcareCdnScaleCrop?: boolean
    },
  ) =>
    retrieveProductImagePath({
      url,
      returnDefault,
      options,
      enableImageKit,
      imagePath,
    })

  const getProductImageBlurDataURL = (url?: string | null) => {
    if (isEmpty(url) || url === DEFAULT_PRODUCT_IMAGE_URL) {
      return undefined
    }

    if (url?.includes('ucarecdn')) {
      // Ultra-small, ultra-fast blur placeholder
      return `${stripUploadcareFilename(url)}/-/resize/300x300/-/quality/lightest/-/format/webp/-/progressive/yes/`
    }

    if (enableImageKit) {
      // Ultra-small, ultra-fast blur placeholder using ImageKit
      const imageKitParams = `w-500,h-500,q-1.5,f-webp,bl-3`
      if (url?.includes('http')) {
        // return `https://ik.imagekit.io/${IMAGEKIT_ID}/${url}?tr=${imageKitParams}`
        return url
      }
      return `https://ik.imagekit.io/${IMAGEKIT_ID}/${imagePath}product/${url}.jpg?tr=${imageKitParams}`
    }

    // Ultra-small, ultra-fast blur placeholder using Weserv
    const weserveParams = `&w=500&h=500&q=1&output=webp&blur=3&il`
    if (url?.includes('http')) {
      return `https://images.weserv.nl/?url=${url}${weserveParams}`
    }
    return `https://images.weserv.nl/?url=${imagePath}product/${url}.jpg${weserveParams}`
  }

  return {
    getProductImagePath,
    getProductImageBlurDataURL,
  }
}

export default useGetProductImagePath
